import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogCancelButton } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { LockedAutocomplete } from "@/components/LockedAutocomplete";
import type { HeroSupplier, HeroSupplierContact } from "@/lib/orders";
import type { MaPosition } from "@/lib/material";
import type { OrderSupplierState } from "@/lib/orderDraftUtils";
import {
  contactToAutocompleteOption,
  defaultContactWhenNoPersons,
  loadOrderSupplierContactOptions,
  loadSupplierContactChoices,
  loadOrderSupplierOptions,
  supplierFieldsFromContact,
  supplierToAutocompleteOption,
} from "@/lib/orderSupplierAutocomplete";
import {
  maPositionOrderHint,
  maPositionOrderState,
} from "@/pages/material/maListDisplay";

export interface MaOrderSupplierConfirm {
  supplier: OrderSupplierState;
  positionIds: number[];
}

export interface MaOrderSupplierDialogProps {
  open: boolean;
  maNummer: string;
  positionen: MaPosition[];
  loading?: boolean;
  onClose: () => void;
  onConfirm: (result: MaOrderSupplierConfirm) => void;
}

function isSupplierReady(supplier: OrderSupplierState | null): boolean {
  if (!supplier) return false;
  if (supplier.id != null && String(supplier.id).trim() !== "") return true;
  return !!(supplier.name || "").trim();
}

function positionIsLinked(p: MaPosition): boolean {
  return maPositionOrderState(p) !== "offen";
}

function buildSelection(positionen: MaPosition[]): Record<number, boolean> {
  const next: Record<number, boolean> = {};
  for (const p of positionen) {
    if (p.id == null) continue;
    next[p.id] = !positionIsLinked(p);
  }
  return next;
}

export function MaOrderSupplierDialog({
  open,
  maNummer,
  positionen,
  loading = false,
  onClose,
  onConfirm,
}: MaOrderSupplierDialogProps) {
  const [supplier, setSupplier] = useState<OrderSupplierState | null>(null);
  const supplierPickSeq = useRef(0);
  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setSupplier(null);
      setSelected({});
      setError(null);
      return;
    }
    setSelected(buildSelection(positionen));
    setError(null);
  }, [open, positionen]);

  const supplierValue = useMemo(() => supplierToAutocompleteOption(supplier), [supplier]);
  const contactValue = useMemo(() => contactToAutocompleteOption(supplier), [supplier]);
  const hasSupplier = isSupplierReady(supplier);
  const selectedIds = useMemo(
    () =>
      positionen
        .filter((p) => p.id != null && selected[p.id])
        .map((p) => p.id),
    [positionen, selected],
  );
  const allSelected =
    positionen.length > 0 && positionen.every((p) => p.id != null && selected[p.id]);

  function patchSupplier(patch: Partial<OrderSupplierState> | null) {
    if (patch === null) {
      setSupplier(null);
      return;
    }
    setSupplier((prev) => ({
      ...(prev || { id: null, name: "" }),
      ...patch,
    }));
  }

  async function selectSupplier(opt: { value: HeroSupplier } | null) {
    if (!opt) {
      supplierPickSeq.current += 1;
      patchSupplier(null);
      return;
    }
    const sameId = supplier?.id != null && String(supplier.id) === String(opt.value.id);
    const companyEmail = (opt.value.email || "").trim();
    if (sameId) {
      patchSupplier({
        id: opt.value.id,
        name: opt.value.name || "",
        company_email: companyEmail || supplier?.company_email || "",
      });
      return;
    }
    const seq = ++supplierPickSeq.current;
    const next: OrderSupplierState = {
      id: opt.value.id,
      name: opt.value.name || "",
      company_email: companyEmail,
      anrede: "",
      email: "",
      contact_first_name: "",
      contact_last_name: "",
      person: "",
    };
    setSupplier(next);
    try {
      const loaded = await loadSupplierContactChoices(next, "");
      if (supplierPickSeq.current !== seq || !loaded.contactsLoaded) return;
      const general = defaultContactWhenNoPersons(loaded.options);
      if (!general) return;
      const fields = supplierFieldsFromContact(general);
      setSupplier((cur) => {
        if (supplierPickSeq.current !== seq) return cur;
        if (!cur || String(cur.id) !== String(next.id)) return cur;
        const alreadyChosen = [cur.email, cur.contact_first_name, cur.contact_last_name, cur.person].some(
          (part) => (part || "").trim() !== "",
        );
        if (alreadyChosen) return cur;
        return { ...cur, ...fields };
      });
    } catch {
      // Ohne geladene Ansprechpartner bleibt die Auswahl leer.
    }
  }

  function toggleAll(checked: boolean) {
    const next: Record<number, boolean> = {};
    for (const p of positionen) {
      if (p.id == null) continue;
      next[p.id] = checked;
    }
    setSelected(next);
  }

  const allowEmptyPositions = positionen.length === 0;

  function handleConfirm() {
    if (!isSupplierReady(supplier)) return;
    if (!allowEmptyPositions && !selectedIds.length) {
      setError("Bitte mindestens eine Position auswählen.");
      return;
    }
    setError(null);
    onConfirm({ supplier: supplier!, positionIds: selectedIds });
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      dirty={supplier != null || selectedIds.length > 0}
      size="lg"
      title="Bestellung aus Materialanforderung"
      description={
        allowEmptyPositions
          ? `Materialanforderung ${maNummer} – Lieferant wählen (keine Positionszeilen).`
          : `Materialanforderung ${maNummer} – Positionen und Lieferant wählen.`
      }
      footer={
        <>
          <DialogCancelButton onClose={onClose} disabled={loading} variant="ghost" />
          <Button
            variant="primary"
            loading={loading}
            disabled={
              !hasSupplier ||
              loading ||
              (!allowEmptyPositions && selectedIds.length === 0)
            }
            onClick={handleConfirm}
          >
            Bestellung vorbereiten
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <section className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-semibold text-label">Positionen</p>
            {positionen.length > 0 ? (
              <button
                type="button"
                className="text-xs text-accent hover:underline"
                onClick={() => toggleAll(!allSelected)}
              >
                {allSelected ? "Keine auswählen" : "Alle auswählen"}
              </button>
            ) : null}
          </div>
          <p className="text-xs text-label-secondary">
            {allowEmptyPositions
              ? "Diese Anforderung hat keine Positionszeilen (nur Freitext/Anhänge). Die Bestellung wird trotzdem angelegt – Positionen können Sie im Entwurf ergänzen."
              : "Markieren Sie die Positionen für diesen Lieferanten. Bereits bestellte Positionen bleiben wählbar, sind aber gekennzeichnet."}
          </p>

          {error ? <p className="text-sm text-status-red">{error}</p> : null}

          {positionen.length === 0 ? (
            <p className="rounded-ios border border-separator bg-fill-quaternary/15 px-3 py-2 text-sm text-label-secondary">
              Keine Positionen vorhanden – Bestellung mit Lieferant und MA-Bezug ist trotzdem möglich.
            </p>
          ) : (
            <div className="max-h-64 space-y-2 overflow-y-auto pr-1">
              {positionen.map((p, idx) => {
                const linked = positionIsLinked(p);
                const hint = maPositionOrderHint(p);
                const orderState = maPositionOrderState(p);
                const checked = !!selected[p.id];
                const menge =
                  p.menge != null && Number.isFinite(Number(p.menge))
                    ? String(p.menge)
                    : "–";
                const einheit = (p.einheit || "").trim();
                return (
                  <label
                    key={p.id}
                    className={`flex cursor-pointer items-start gap-2 rounded-ios border px-3 py-2 text-sm ${
                      linked
                        ? "border-indigo/30 bg-indigo/5"
                        : "border-separator bg-card"
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 rounded border-separator-opaque text-accent focus:ring-accent-soft"
                      checked={checked}
                      onChange={(e) =>
                        setSelected((prev) => ({ ...prev, [p.id]: e.target.checked }))
                      }
                    />
                    <span className="min-w-0 flex-1 break-words [overflow-wrap:anywhere]">
                      <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                        <span className="text-label-secondary">#{idx + 1}</span>
                        <span className="font-medium text-label">
                          {menge}
                          {einheit ? ` ${einheit}` : ""}
                        </span>
                        <span className="min-w-0 text-label">{p.text || "–"}</span>
                      </span>
                      {orderState === "bereitgelegt" ? (
                        <span className="mt-0.5 block text-xs text-status-green">
                          bereits im Lager bereitgelegt
                        </span>
                      ) : hint ? (
                        <span
                          className={`mt-0.5 block text-xs ${
                            orderState === "bestellt" || orderState === "geliefert"
                              ? "text-indigo"
                              : "text-accent"
                          }`}
                        >
                          {orderState === "bestellt" || orderState === "geliefert"
                            ? "bereits in "
                            : ""}
                          {hint}
                        </span>
                      ) : null}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </section>

        <section className="space-y-3 border-t border-separator pt-3">
          <p className="text-sm font-semibold text-label">Lieferant</p>
          <LockedAutocomplete<HeroSupplier>
            label="Lieferant *"
            required
            placeholder="Schüco, Marx, …"
            value={supplierValue}
            minChars={0}
            loadOptions={loadOrderSupplierOptions}
            onChange={(opt) => {
              void selectSupplier(opt);
            }}
          />
          <LockedAutocomplete<HeroSupplierContact>
            label="Ansprechpartner"
            placeholder="Name oder E-Mail …"
            value={contactValue}
            minChars={0}
            disabled={!hasSupplier}
            disabledHint="Bitte zuerst einen Lieferanten wählen."
            loadOptions={(q) => loadOrderSupplierContactOptions(supplier, q)}
            onChange={(opt) => {
              if (!opt) {
                patchSupplier({
                  anrede: "",
                  email: "",
                  contact_first_name: "",
                  contact_last_name: "",
                  person: "",
                });
                return;
              }
              patchSupplier(supplierFieldsFromContact(opt.value));
            }}
          />
        </section>
      </div>
    </Dialog>
  );
}
